import { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { Loader2, Check, ChevronDown, MapPin } from "lucide-react";
import { useFirstFreightPlaces } from "../hooks/useFirstFreightPlaces";

/**
 * PlaceSearchDropdown - Reusable component for searching First Freight suburbs
 * Used in both CreateWaybillPage and SettingsPage for address verification
 */
const PlaceSearchDropdown = ({ 
  value, 
  onChange, 
  onPlaceSelect, 
  placeholder = "Search suburb...",
  showValidation = true,
  showSavedValue = false,  // If true, shows saved value from props
  dropdownPosition = "left"  // "left" or "right" - controls which side dropdown aligns to
}) => {
  // Initialize with saved value if showSavedValue is true
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const { searchByName } = useFirstFreightPlaces();
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);

  // Initialize with saved value on mount
  useEffect(() => {
    if (!initialized && showSavedValue && value?.town) {
      setInputValue(value.town);
      if (value.place) {
        setSelectedPlace(value);
      }
      setInitialized(true);
    }
  }, [value, showSavedValue, initialized]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = async (e) => {
    const query = e.target.value;
    setInputValue(query);
    
    // Clear selected place when user starts typing new search
    if (selectedPlace && query !== selectedPlace.town) {
      setSelectedPlace(null);
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (query.length >= 2) {
      setSearching(true);
      timeoutRef.current = setTimeout(async () => {
        try {
          const result = await searchByName(query);
          setSearching(false);
          timeoutRef.current = null;
          if (result && result.success && result.places?.length > 0) {
            // Filter out Boxes/Busse/PO Box results - these are not street addresses
            const excludeTerms = ['boxes', 'busse', 'box ', 'p.o.', 'p o box', 'posbus'];
            const filteredPlaces = result.places.filter(place => {
              const townLower = (place.town || '').toLowerCase();
              return !excludeTerms.some(term => townLower.includes(term));
            });
            setSuggestions(filteredPlaces);
            setShowDropdown(filteredPlaces.length > 0);
          } else {
            setSuggestions([]);
            setShowDropdown(false);
          }
        } catch (err) {
          console.error('Place search error:', err);
          setSearching(false);
          timeoutRef.current = null;
        }
      }, 50);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (place) => {
    setInputValue(place.town);
    setSelectedPlace(place);
    onChange?.(place.town);
    onPlaceSelect?.(place);
    setShowDropdown(false);
    setSuggestions([]);
  };

  // Show as valid if place was selected (either from props or user selection)
  const isValid = selectedPlace !== null && selectedPlace.place !== null;

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={`h-7 text-sm pr-8 ${isValid ? 'border-green-500 bg-green-50' : ''}`}
          data-testid="place-search-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {searching && <Loader2 className="w-5 h-5 animate-spin text-green-500" />}
          {isValid && !searching && <Check className="w-4 h-4 text-green-500" />}
          {!searching && !isValid && <ChevronDown className="w-3 h-3 text-gray-400" />}
        </div>
      </div>
      
      {showValidation && isValid && selectedPlace && (
        <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {selectedPlace.town} (Code: {selectedPlace.pcode || 'N/A'})
        </div>
      )}
      
      {showDropdown && suggestions.length > 0 && (
        <div 
          className={`absolute z-50 top-full mt-1 w-64 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-y-auto ${
            dropdownPosition === "right" ? "right-0" : "left-0"
          }`}
        >
          {suggestions.map((place, idx) => (
            <button
              key={`${place.place}-${idx}`}
              type="button"
              onClick={() => handleSelect(place)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 flex items-center justify-between border-b border-gray-100 last:border-0"
            >
              <span className="font-medium">{place.town}</span>
              <span className="text-xs text-gray-400">{place.pcode || '-'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlaceSearchDropdown;
